import { Badge, Box, Typography, type TooltipProps } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import {
    labelWithHotkey,
    type ToolEntry,
} from '~/components/toolbar/shared/toolEntry';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';

interface ToolEntryButtonProps {
    entry: ToolEntry;
    tooltipPlacement?: TooltipProps['placement'];
    /** The widest the tooltip grows when it carries the entry's hint, px. */
    hintMaxWidthPx?: number;
    /** A caution: a sign on the button, and `text` by its name over `detail`. */
    warning?: { text: string; detail: string };
    /** Defaults to `tool-<id>`. */
    'data-testid'?: string;
}

/**
 * A toolbar button for one {@link ToolEntry}: a click toggles it, and the
 * tooltip states the refusal while it is disabled, else its name with its
 * key over any usage hint. A disabled button announces its refusal; an
 * enabled one its name, without the key or hint. A held tool is
 * highlighted, and its tooltip and description say it waits for the open
 * operation, while its entry is enabled.
 */
export default function ToolEntryButton({
    entry,
    tooltipPlacement,
    hintMaxWidthPx,
    warning,
    'data-testid': testId = `tool-${entry.id}`,
}: ToolEntryButtonProps) {
    const intl = useIntl();
    const name = labelWithHotkey(entry);
    const held = entry.held && !entry.disabled;
    const heldNote = held
        ? intl.formatMessage({ id: 'toolbar.tool.heldHint' })
        : undefined;
    const note = heldNote ?? entry.hint;
    const warningText = warning && `${warning.text}: ${warning.detail}`;
    let tooltip: ReactNode = name;
    if (entry.disabled) {
        tooltip = buildToolTooltip(intl, entry.label, entry.disabledReason);
    } else if (note || warning) {
        tooltip = (
            <Box sx={{ maxWidth: hintMaxWidthPx }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {warning && (
                        <Box
                            component={AlertTriangle}
                            aria-hidden
                            size={12}
                            sx={{ flexShrink: 0, color: 'warning.main' }}
                        />
                    )}
                    <Typography fontSize={11} noWrap={Boolean(warning)}>
                        {name}
                    </Typography>
                    {warning && (
                        <Typography
                            variant="monoMeta"
                            noWrap
                            sx={{ color: 'warning.main' }}
                        >
                            {warning.text}
                        </Typography>
                    )}
                </Box>
                {warning && (
                    <Typography
                        variant="monoMeta"
                        sx={{ display: 'block', color: 'warning.main' }}
                    >
                        {warning.detail}
                    </Typography>
                )}
                <Typography
                    component="span"
                    sx={{ fontSize: 10, opacity: 0.7 }}
                >
                    {note}
                </Typography>
            </Box>
        );
    }
    const button = (
        <ToolbarButton
            icon={entry.icon}
            tooltip={tooltip}
            ariaLabel={entry.disabled ? undefined : entry.label}
            tooltipPlacement={tooltipPlacement}
            selected={entry.selected}
            flashing={held}
            description={
                [heldNote, warningText].filter(Boolean).join(' ') || undefined
            }
            disabled={entry.disabled}
            onClick={entry.toggle}
            data-testid={testId}
        />
    );
    return warning ? (
        <Badge
            data-testid={`${testId}-warning`}
            badgeContent={<AlertTriangle size={13} strokeWidth={2.5} />}
            sx={(theme) => ({
                '& .MuiBadge-badge': {
                    p: 0,
                    minWidth: 0,
                    height: 'auto',
                    pointerEvents: 'none',
                    color: theme.palette.warning.main,
                    '& svg': { fill: theme.palette.background.default },
                },
            })}
        >
            {button}
        </Badge>
    ) : (
        button
    );
}
